/**
 * Dropstack Plus extras — MOCK stubs for the web prototype.
 *
 * Plus currently removes ads (services/ads.ts reads `entitlements.adsRemoved`,
 * which an active Plus sets). The other three perks the store lists (practice
 * packs, bonus themes, a parent report) are not built yet. This module is
 * where they plug in: each method is gated on `Entitlements.plus`, and
 * `isAvailable` is false for all three, so the store sheet shows them as
 * "coming soon" instead of promising something the app doesn't provide.
 *
 * To ship a perk: implement its method (practice packs run as unranked local
 * runs outside POST /api/sessions; bonus themes are cosmetic ThemePacks; the
 * parent report reads local progress and the player's own runs), then flip its
 * entry in PLUS_FEATURES_AVAILABLE. See docs/MONETIZATION.md § 3.1.
 */
import type { ParentReport, PlusFeatureId, PlusFeatures, PracticePack, PurchaseService } from '../contracts.ts';

/** Which Plus extras exist in this build. All false: the prototype only removes ads. */
export const PLUS_FEATURES_AVAILABLE: Readonly<Record<PlusFeatureId, boolean>> = Object.freeze({
  practicePacks: false,
  bonusThemes: false,
  parentReport: false,
});

/** The Plus extras, in the order the store lists them. */
export const PLUS_FEATURE_IDS: readonly PlusFeatureId[] = ['practicePacks', 'bonusThemes', 'parentReport'];

export interface PlusFeaturesOptions {
  /** Override which features exist (tests, or a build that ships one early). */
  available?: Partial<Record<PlusFeatureId, boolean>>;
}

export function createPlusFeatures(purchases: Pick<PurchaseService, 'getEntitlements'>, options: PlusFeaturesOptions = {}): PlusFeatures {
  const available = { ...PLUS_FEATURES_AVAILABLE, ...options.available };
  const isAvailable = (feature: PlusFeatureId): boolean => available[feature];
  const isUnlocked = (feature: PlusFeatureId): boolean => isAvailable(feature) && purchases.getEntitlements().plus;
  /** `build()` for a player with Plus (and a feature this build has), otherwise `locked`. */
  const gated = async <T>(feature: PlusFeatureId, locked: T, build: () => Promise<T>): Promise<T> =>
    isUnlocked(feature) ? build() : locked;
  return {
    isAvailable,
    isUnlocked,
    // The `build` callbacks are the stubs to replace when a perk ships.
    practicePacks: () => gated<PracticePack[]>('practicePacks', [], async () => []),
    bonusThemes: () => gated<string[]>('bonusThemes', [], async () => []),
    parentReport: () => gated<ParentReport | null>('parentReport', null, async () => null),
  };
}
